import { TOOLS, type TToolKey } from "../appConsts";
import { TLicenseType } from "./mongo";

export const notificationPermissionsDefault = {
    morning_update: true,
    market_wrap: true,
    alert_to_lock: true,
    saturday: true,
    housing_survey: true,
};
export type TNotificationPermissions = typeof notificationPermissionsDefault;

export const navigationPermissionsDefault = Object.fromEntries(
    TOOLS.filter((tool) => tool.toolKey != null).map((tool) => [tool.toolKey, true]),
) as Record<TToolKey, boolean>;
export type TNavigationPermissions = typeof navigationPermissionsDefault;

export const restrictionsPermissionsDefault = {
    allowProductSwitcher: true,
    allowReportMessage: true,
    allowSiteBanners: true,
    approvalEmailCC: "",
    approvalEmailTo: "",
    blockAlerts: false,
    coBrandApprove: true,
    compLogos: "",
    corpPalettesOnly: false,
    defaultLicenseLabel: "nmls" as TLicenseType,
    delayOffset: 0,
    sendMarketingKitToClient: true,
    disableOHFCobrand: false,
    disclaimerBefore: true,
    editableDisclaimer: true,
    forceLicenseLabel: false,
    forceSocialStudioFrames: false,
    headShotEdit: true,
    hideMbsScripts: false,
    hideSSFrames: false,
    isEnterprise: true,
    langVis: true,
    markDatEmail: true,
    mbsDisclaimer: true,
    poweredBy: true,
    removeSocialStudioCustomEdit: false,
    requireSharePasscode: false,
    requireStateLic: false,
    screenShareVis: true,
    sendForApproval: false,
    showProductSwitcherHR: true,
    showUSDALoanProgram: true,
    upDateHeader: true,
    useCorpLogo: false,
    useInvoice: true,
    widgetBuild: false,
};
export type TRestrictionsPermissions = typeof restrictionsPermissionsDefault;

export type TInferredRestrictions = {
    hideDownloadMarketWrap: boolean;
    hideTaxLetter: boolean;
};

export type TResolvedRestrictions = TRestrictionsPermissions & TInferredRestrictions;

export interface IMBSCorpPermissions {
    _id?: string;
    corpID: string;
    notifications: TNotificationPermissions;
    navigation: TNavigationPermissions;
    restrictions: TRestrictionsPermissions;
    createdAt?: Date;
    updatedAt?: Date;
}

export type TPermissionCategory = keyof Omit<
    IMBSCorpPermissions,
    "_id" | "corpID" | "createdAt" | "updatedAt"
>;

export interface ICorpPalette {
    paletteName: string;
    primaryColorHex: string;
    secondaryColorHex: string;
}

export type ICorpPermissionsWithPalette = Omit<IMBSCorpPermissions, "_id" | "createdAt" | "updatedAt"> & {
    palettes: ICorpPalette[];
};

export interface ICorp {
    id: string;
    logo: string;
    cc: string;
    morningUpdate: {
        startYear: number;
        startMonth: number;
    };
    permissions: Omit<IMBSCorpPermissions, "restrictions"> & { restrictions: TResolvedRestrictions };
    palettes: ICorpPalette[];
}

export const WELLS_FARGO_CORP_ID = "ba0b71d0-d02e-40f8-8341-7e464e8d5124";

export interface IPermissionMeta {
    label: string;
    description: string;
}

export const permissionsInfo: Partial<Record<string, IPermissionMeta>> = {
    // Notifications
    morning_update: { label: "Morning Update", description: "Morning Update email" },
    market_wrap: { label: "Market Wrap", description: "Market Wrap email" },
    alert_to_lock: { label: "Alert to Lock", description: "Alert to Lock email" },
    saturday: { label: "Saturday", description: "Saturday email" },
    housing_survey: { label: "Housing Survey", description: "Housing Survey email" },
    // Restrictions
    allowProductSwitcher: {
        label: "Allow Product Switcher",
        description: "Allows users to switch between products",
    },
    allowReportMessage: { label: "Allow Report Message", description: "Allows a custom message on reports" },
    allowSiteBanners: {
        label: "Allow Site Banners",
        description: "Allows site-wide banners to be displayed",
    },
    approvalEmailCC: { label: "Approval Email CC", description: "CC recipients for approval emails" },
    approvalEmailTo: { label: "Approval Email To", description: "Primary recipient for approval emails" },
    coBrandApprove: {
        label: "Co-Brand Approved",
        description: "Allow corp users to use cobranding",
    },
    compLogos: { label: "Comp Logos", description: "Comp Logos" },
    corpPalettesOnly: {
        label: "Corp Palettes Only",
        description: "Restricts users to corp-defined color palettes only",
    },
    defaultLicenseLabel: {
        label: "Default License Label",
        description: "The default license label type (nmls or license)",
    },
    delayOffset: {
        label: "Delay Offset (minutes)",
        description: "Minutes to delay this corp's text alerts (0 = no delay)",
    },
    sendMarketingKitToClient: {
        label: "Send Marketing Kit to Client",
        description: "Allows users to send Marketing Kit campaigns directly to clients",
    },
    disableOHFCobrand: { label: "Disable OHF Cobrand", description: "Disable OHF Cobrand" },
    disclaimerBefore: {
        label: "Disclaimer Before",
        description: "Positions the disclaimer before report content",
    },
    editableDisclaimer: {
        label: "Editable Disclaimer",
        description: "Allows users to edit the disclaimer text",
    },
    forceLicenseLabel: {
        label: "Force License Label",
        description: "Forces the corp license label type on all users",
    },
    forceSocialStudioFrames: {
        label: "Force Social Studio Frames",
        description: "Forces Social Studio frames for all users",
    },
    headShotEdit: { label: "Head Shot Edit", description: "Allows users to edit their headshot" },
    blockAlerts: {
        label: "Block Alerts",
        description: "Blocks alert details and stance indicators from users",
    },
    hideMbsScripts: { label: "Hide MBS Scripts", description: "Hides MBS script content from users" },
    hideSSFrames: { label: "Hide SS Frames", description: "Hides Social Studio frames" },
    isEnterprise: { label: "Is Enterprise", description: "Marks the corporation as an enterprise account" },
    langVis: { label: "Lang Vis", description: "Lang Vis" },
    markDatEmail: { label: "Market Data Email Visible", description: "Allow emails with market data" },
    mbsDisclaimer: { label: "MBS Disclaimer", description: "Includes the MBS disclaimer on reports" },
    poweredBy: { label: "Powered By", description: "Shows the Powered By branding" },
    removeSocialStudioCustomEdit: {
        label: "Remove Social Studio Custom Edit",
        description: "Removes the custom edit option in Social Studio",
    },
    requireSharePasscode: {
        label: "Require Share Passcode",
        description: "Requires a passcode to view shared reports",
    },
    requireStateLic: {
        label: "Require State License",
        description: "Requires users to have a state license on file",
    },
    screenShareVis: { label: "Screen Share Vis", description: "Screen Share Vis" },
    sendForApproval: {
        label: "Send for Approval",
        description: "Sends co-brand requests through an approval workflow",
    },
    showProductSwitcherHR: { label: "Show Product Switcher HR", description: "Show Product Switcher HR" },
    showUSDALoanProgram: {
        label: "Show USDA Loan Program",
        description: "Shows the USDA loan program option",
    },
    upDateHeader: {
        label: "Update Header",
        description: "Allows the update header to be displayed (does not affect headshots)",
    },
    useCorpLogo: {
        label: "Use Corp Logo",
        description: "Uses the corp logo instead of the user's own logo",
    },
    useInvoice: { label: "Use Invoice", description: "Use Invoice" },
    widgetBuild: { label: "Widget Build", description: "Allows users to build embeddable widgets" },
};
